import * as React from 'react';
import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import type { FC } from 'react';
import { translation } from 'app/helpers/createLocalesContext';
import { LocalesContext } from './locales';
import NameFilter from './NameFilter';
import ProviderFilter from './ProviderFilter';
import SearchEnd from './SearchEnd';
import { CasinoFiltersProps } from './types';
import useCasinoFilters from './useCasinoFilters';
import iconNew from './images/icon_new.svg';
import iconTop from './images/icon_hot.svg';
import iconFavorites from './images/icon_popular.svg';
import iconSearch from './images/icon_search.svg';

const CasinoFilters: FC<CasinoFiltersProps> = (props) => {
  const { filters, setFilter, reset, resets } = useCasinoFilters(props.filters);
  const nameInput = useRef<HTMLInputElement>(null);
  // Shown by the search icon: the search layout before there is anything to search for (SearchEnd).
  const [searchFieldShown, setSearchFieldShown] = useState(false);

  // The search icon stands for the search field, hidden on phones until a search; the search
  // itself starts with a typed name. The focus is made within the click: iOS opens the keyboard
  // only for such a focus. The field has to be shown by then (flushSync): a hidden field takes no
  // focus.
  const openSearchField = () => {
    flushSync(() => setSearchFieldShown(true));
    nameInput.current?.focus({ preventScroll: true });
  };

  const resetSearch = () => {
    reset();
    setSearchFieldShown(false);
  };

  return (
    <LocalesContext.Provider value={props.locales}>
      <div className={`casino-filters rs-flex${searchFieldShown ? ' casino-filters__field-shown' : ''}`}>
        <div className="rs-flex-item rs-medium-6 rs-large-6 rs-xlarge-4 filters-dropdowns">
          <div className="filters_provider">
            <ProviderFilter
              value={filters.provider}
              onChange={(v) => setFilter({ provider: v })}
              providerInfos={props.providers}
            />
          </div>
        </div>
        <div className="rs-flex-item rs-large-12 rs-xlarge-4 filters_switchers">
          <button
            type="button"
            onClick={openSearchField}
            className="xo-icon-button mobile-search-button rs-hide-large-up"
          >
            <img src={iconSearch} alt={translation(props.locales, 'search')} />
          </button>
          <button
            className="xo-button xo-button_blue xo-button_icon-text xo-button_full-width"
            type="button"
            onClick={() => setFilter({ list: 'new', tag: '' })}
          >
            <img src={iconNew} alt="" />
            <span>{translation(props.locales, 'category_names.new')}</span>
          </button>
          <button
            className="xo-button xo-button_blue xo-button_icon-text xo-button_full-width"
            type="button"
            onClick={() => setFilter({ list: 'top', tag: '' })}
          >
            <img src={iconTop} alt="" />
            <span>{translation(props.locales, 'category_names.top')}</span>
          </button>
          {props.playerPresent && (
            <button
              className="xo-button xo-button_blue xo-button_icon-text xo-button_full-width"
              type="button"
              onClick={() => setFilter({ list: 'favorites', tag: '' })}
            >
              <img src={iconFavorites} alt="" />
              <span>{translation(props.locales, 'category_names.favorites')}</span>
            </button>
          )}
        </div>
        <div className="rs-flex-item rs-medium-6 rs-large-6 rs-xlarge-4 f-search-wrapper">
          <div className="filters_search f-search">
            <NameFilter
              value={filters.name}
              onChange={(v) => setFilter({ name: v })}
              resets={resets}
              inputRef={nameInput}
            />
          </div>
        </div>
        <SearchEnd
          searchActive={props.searchActive}
          searchFieldShown={searchFieldShown}
          banner={props.banner}
          onReset={resetSearch}
        />
      </div>
    </LocalesContext.Provider>
  );
};

export default CasinoFilters;
